import { z } from 'zod';
import { isoDateSchema } from './people.js';

export const DAY_PERIOD = ['MORNING', 'AFTERNOON', 'EVENING'] as const;
export const dayPeriodSchema = z.enum(DAY_PERIOD);
export type DayPeriod = z.infer<typeof dayPeriodSchema>;

export const DAY_PERIOD_LABEL: Record<DayPeriod, string> = {
  MORNING: 'Manhã',
  AFTERNOON: 'Tarde',
  EVENING: 'Noite',
};

/** 0 = domingo, como no JavaScript e como a igreja conta a semana. */
export const WEEKDAY_LABEL = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
] as const;

export const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

export const UNAVAILABILITY_REASON = [
  'TRAVEL',
  'WORK',
  'STUDY',
  'HEALTH',
  'FAMILY',
  'OTHER',
] as const;
export const unavailabilityReasonSchema = z.enum(UNAVAILABILITY_REASON);
export type UnavailabilityReason = z.infer<typeof unavailabilityReasonSchema>;

export const UNAVAILABILITY_REASON_LABEL: Record<UnavailabilityReason, string> = {
  TRAVEL: 'Viagem',
  WORK: 'Trabalho',
  STUDY: 'Estudo ou prova',
  HEALTH: 'Saúde',
  FAMILY: 'Família',
  OTHER: 'Outro motivo',
};

export const availabilityRuleSchema = z.object({
  weekday: z.number().int().min(0).max(6),
  period: dayPeriodSchema,
  available: z.boolean(),
});
export type AvailabilityRule = z.infer<typeof availabilityRuleSchema>;

/** A grade inteira vai de uma vez: é assim que a tela salva. */
export const setAvailabilitySchema = z.object({
  rules: z.array(availabilityRuleSchema).max(21),
});
export type SetAvailabilityInput = z.infer<typeof setAvailabilitySchema>;

export const createUnavailabilitySchema = z
  .object({
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    reason: unavailabilityReasonSchema.default('OTHER'),
    note: z.string().trim().max(300).nullish(),
  })
  .refine((dados) => dados.endsOn >= dados.startsOn, {
    path: ['endsOn'],
    message: 'A data final não pode ser antes da inicial',
  });
export type CreateUnavailabilityInput = z.infer<typeof createUnavailabilitySchema>;

export const unavailabilitySchema = z.object({
  id: z.string().uuid(),
  personId: z.string().uuid(),
  personName: z.string().optional(),
  startsOn: z.string(),
  endsOn: z.string(),
  reason: unavailabilityReasonSchema,
  note: z.string().nullable(),
  days: z.number().int(),
  createdAt: z.string().datetime(),
});
export type Unavailability = z.infer<typeof unavailabilitySchema>;

export const personLimitsSchema = z.object({
  maxPerWeek: z.coerce.number().int().min(1).max(21).nullish(),
  maxPerMonth: z.coerce.number().int().min(1).max(60).nullish(),
});
export type PersonLimitsInput = z.infer<typeof personLimitsSchema>;

export const availabilityOverviewSchema = z.object({
  rules: z.array(availabilityRuleSchema),
  unavailabilities: z.array(unavailabilitySchema),
  limits: personLimitsSchema,
});
export type AvailabilityOverview = z.infer<typeof availabilityOverviewSchema>;

/** Quantos dias o bloqueio cobre, contando as duas pontas. */
export function diasDeBloqueio(startsOn: string, endsOn: string): number {
  const inicio = Date.parse(`${startsOn}T12:00:00Z`);
  const fim = Date.parse(`${endsOn}T12:00:00Z`);
  return Math.round((fim - inicio) / 86_400_000) + 1;
}

/** A data cai dentro de algum bloqueio? Usado ao escalar e no calendário. */
export function estaBloqueado(
  data: string,
  bloqueios: ReadonlyArray<{ startsOn: string; endsOn: string }>,
): boolean {
  return bloqueios.some((bloqueio) => data >= bloqueio.startsOn && data <= bloqueio.endsOn);
}
